/**
 * Utility class for lazily getting a variable.
 */
export class LazyValue<T> {
    private val?: T;
    private prom?: Promise<T>;
    private done = false;

    public constructor(private getFn: () => Promise<T>) {}

    /**
     * Whether or not a cached value is present.
     */
    public get present(): boolean {
        // we use a tracking variable just in case the final value is falsy
        return this.done;
    }

    /**
     * Gets the value without invoking a get. May be undefined until the
     * value is fetched properly.
     */
    public get cachedValue(): T | undefined {
        return this.val;
    }

    /**
     * Gets a promise which resolves to the value, eventually.
     * Rejected promises are NOT cached: a subsequent call to .value will retry.
     */
    public get value(): Promise<T> {
        if (this.prom) return this.prom;
        this.prom = this.getFn().then(
            (v) => {
                this.val = v;
                this.done = true;
                return v;
            },
            (err) => {
                // Clear the cached promise so the next access retries instead of
                // immediately re-throwing the same (possibly transient) error.
                this.prom = undefined;
                throw err;
            },
        );
        return this.prom;
    }
}
